/* Created by Interactive 63 - https://interactive63.com */
/**
 * The easing editor for the selected keyframes: tabs for each easing family, a graph with
 * draggable control handles, a live preview strip, named curve chips, spring presets, saved
 * easings and a copy-as-CSS button. The helpers above the control are also used by the timeline's
 * context menu.
 */
import { evaluateEasing } from '../../../core/easing/evaluate';
import { NAMED_CURVES, SPRING_PRESETS } from '../../../core/easing/named-curves';
import { easingToCss } from '../../../core/easing/to-css';
import type { Easing } from '../../../core/model/types';
import {
  loadSavedEasings,
  removeSavedEasing,
  saveEasing,
  type SavedEasing,
} from '../../saved-easings';
import { svgEl, h } from '../../ui/dom';
import { toast } from '../../ui/toast';
import {
  GRAPH_SIZE,
  Y_MAX,
  Y_MIN,
  controlPointsOf,
  dragControlPoint,
  sameEasing,
  toGraph,
} from './easing-graph';
import { NumberInput } from './inputs/number-input';

export const EASING_CHOICES = [
  { id: 'linear', label: 'Linear' },
  { id: 'ease', label: 'Ease' },
  { id: 'ease-in', label: 'Ease in' },
  { id: 'ease-out', label: 'Ease out' },
  { id: 'ease-in-out', label: 'Ease in-out' },
  { id: 'cubic', label: 'Custom cubic-bezier' },
  { id: 'steps', label: 'Steps' },
  { id: 'hold', label: 'Hold' },
  { id: 'spring', label: 'Spring' },
  { id: 'bounce', label: 'Bounce' },
  { id: 'elastic', label: 'Elastic' },
] as const;

export type EasingChoice = (typeof EASING_CHOICES)[number]['id'];

const PREVIEW_WIDTH = 120;
const PREVIEW_HEIGHT = 60;

/** Maps an easing to its dropdown choice. */
export function easingChoice(easing: Easing): EasingChoice {
  return easing.type === 'keyword' ? easing.name : easing.type;
}

/** Returns a sensible easing with default parameters for a dropdown choice. */
export function defaultEasingFor(choice: EasingChoice): Easing {
  switch (choice) {
    case 'linear':
    case 'hold':
      return { type: choice };
    case 'ease':
    case 'ease-in':
    case 'ease-out':
    case 'ease-in-out':
      return { type: 'keyword', name: choice };
    case 'cubic':
      return { type: 'cubic', x1: 0.25, y1: 0.1, x2: 0.25, y2: 1 };
    case 'steps':
      return { type: 'steps', count: 4, position: 'jump-end' };
    case 'spring':
      return { type: 'spring', stiffness: 170, damping: 26, mass: 1 };
    case 'bounce':
      return { type: 'bounce', bounces: 3, decay: 0.5 };
    case 'elastic':
      return { type: 'elastic', oscillations: 3, decay: 6 };
  }
}

/** Builds an SVG path of an easing curve, scaled to a preview box with room for overshoot. */
export function curvePath(easing: Easing, width = PREVIEW_WIDTH, height = PREVIEW_HEIGHT): string {
  const samples = 60;
  const values = Array.from({ length: samples + 1 }, (_, i) => evaluateEasing(easing, i / samples));
  const low = Math.min(0, ...values);
  const high = Math.max(1, ...values);
  const pad = 6;
  const span = high - low || 1;
  return values
    .map((value, i) => {
      const x = (i / samples) * width;
      const y = pad + (1 - (value - low) / span) * (height - pad * 2);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

type Tab = 'curve' | 'spring' | 'bounce' | 'elastic' | 'steps' | 'hold' | 'linear';

const TABS: { id: Tab; label: string }[] = [
  { id: 'curve', label: 'Curve' },
  { id: 'spring', label: 'Spring' },
  { id: 'bounce', label: 'Bounce' },
  { id: 'elastic', label: 'Elastic' },
  { id: 'steps', label: 'Steps' },
  { id: 'hold', label: 'Hold' },
  { id: 'linear', label: 'Linear' },
];

const BASIC_CURVES = [
  { label: 'Linear', easing: { type: 'linear' } },
  { label: 'Ease', easing: { type: 'keyword', name: 'ease' } },
  { label: 'Ease in', easing: { type: 'keyword', name: 'ease-in' } },
  { label: 'Ease out', easing: { type: 'keyword', name: 'ease-out' } },
  { label: 'Ease in-out', easing: { type: 'keyword', name: 'ease-in-out' } },
] as const satisfies readonly { label: string; easing: Easing }[];

const EDIT_KEY = 'easing:edit';
const PREVIEW_CYCLE_MS = 1200;
const PREVIEW_PAUSE_MS = 400;

/** Which tab shows an easing. */
function tabOf(easing: Easing): Tab {
  switch (easing.type) {
    case 'keyword':
    case 'cubic':
      return 'curve';
    default:
      return easing.type;
  }
}

/** The easing a tab switches to when it is first chosen. */
function defaultForTab(tab: Tab): Easing {
  switch (tab) {
    case 'curve':
      return { type: 'keyword', name: 'ease' };
    case 'linear':
    case 'hold':
      return { type: tab };
    default:
      return defaultEasingFor(tab);
  }
}

/** The easing as a bounce, keeping its values if it already is one. */
function asBounce(easing: Easing): Extract<Easing, { type: 'bounce' }> {
  return easing.type === 'bounce' ? easing : { type: 'bounce', bounces: 3, decay: 0.5 };
}

/** The easing as an elastic, keeping its values if it already is one. */
function asElastic(easing: Easing): Extract<Easing, { type: 'elastic' }> {
  return easing.type === 'elastic' ? easing : { type: 'elastic', oscillations: 3, decay: 6 };
}

/** The text copied by "Copy CSS": the fallback declaration first when there is one. */
export function cssText(easing: Easing): string {
  const css = easingToCss(easing);
  return css.fallback
    ? `animation-timing-function: ${css.fallback};\nanimation-timing-function: ${css.value};`
    : css.value;
}

/** Builds a path through dense samples of an easing, in graph pixels. */
function graphPath(easing: Easing): string {
  const stepped = easing.type === 'steps' || easing.type === 'hold';
  const samples = stepped ? 240 : 100;
  return Array.from({ length: samples + 1 }, (_, i) => {
    const { px, py } = toGraph(i / samples, evaluateEasing(easing, i / samples));
    return `${i === 0 ? 'M' : 'L'}${px.toFixed(1)} ${py.toFixed(1)}`;
  }).join(' ');
}

export class EasingControl {
  readonly el: HTMLElement;
  private readonly tabs: HTMLElement;
  private readonly body: HTMLElement;
  private readonly graph: SVGSVGElement;
  private readonly curve: SVGPathElement;
  private readonly handles: SVGGElement;
  private readonly dotTrack: HTMLElement;
  private readonly dot: HTMLElement;
  private readonly ghost: HTMLElement;
  private readonly playButton: HTMLButtonElement;
  private easing: Easing | null = null;
  private tab: Tab | null = null;
  private syncs: (() => void)[] = [];
  private saved: SavedEasing[] = loadSavedEasings();
  private frame = 0;
  private previewStart = 0;
  private previewOnce = false;
  private wasConnected = false;
  private idleFrames = 0;
  private readonly reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Builds the control. `onChange` receives the complete new easing and a coalescing key. */
  constructor(private readonly onChange: (easing: Easing, coalesceKey?: string) => void) {
    this.tabs = h('div', { class: 'easing-tabs', role: 'tablist', 'aria-label': 'Easing type' });
    this.body = h('div', { class: 'easing-body' });
    this.curve = svgEl('path', { d: '', class: 'easing-curve' });
    this.handles = svgEl('g', { class: 'easing-handles' });
    this.graph = svgEl(
      'svg',
      {
        class: 'easing-graph',
        viewBox: `0 0 ${GRAPH_SIZE} ${GRAPH_SIZE}`,
        width: GRAPH_SIZE,
        height: GRAPH_SIZE,
        role: 'img',
        'aria-label': 'Easing curve: time across, progress up',
      },
      svgEl('rect', { class: 'easing-frame', x: 0, y: 0, width: GRAPH_SIZE, height: GRAPH_SIZE }),
      svgEl('rect', {
        class: 'easing-unit',
        x: 0,
        y: toGraph(0, 1).py,
        width: GRAPH_SIZE,
        height: toGraph(0, 0).py - toGraph(0, 1).py,
      }),
      this.curve,
      this.handles,
    );
    this.dot = h('span', { class: 'easing-dot' });
    this.ghost = h('span', { class: 'easing-dot easing-dot-ghost' });
    this.dotTrack = h(
      'div',
      { class: 'easing-track', 'aria-hidden': 'true' },
      this.ghost,
      this.dot,
    );
    this.playButton = h('button', { type: 'button', class: 'easing-play' }, 'Play preview');
    this.playButton.hidden = !this.reducedMotion;
    this.playButton.addEventListener('click', () => {
      this.previewOnce = true;
      this.previewStart = 0;
      this.startPreview();
    });
    const copy = h(
      'button',
      { type: 'button', class: 'easing-copy', title: 'Copy as CSS' },
      'Copy CSS',
    );
    copy.addEventListener('click', () => {
      if (this.easing)
        void navigator.clipboard.writeText(cssText(this.easing)).then(() => {
          toast.info('Easing copied.');
        });
    });
    this.el = h(
      'div',
      { class: 'easing-control' },
      this.tabs,
      this.graph,
      this.dotTrack,
      h('div', { class: 'easing-actions' }, this.playButton, copy),
      this.body,
    );
    if (!this.reducedMotion) this.startPreview();
  }

  /** Shows the easing shared by the selected keyframes, or "Mixed" when they differ. */
  setEasings(easings: readonly Easing[]): void {
    const first = easings[0];
    if (!first) return;
    const shown = easings.every((e) => sameEasing(e, first)) ? first : null;
    if (sameEasing(shown, this.easing) && this.tab !== null) return;
    this.easing = shown;
    this.refresh();
  }

  /** Redraws everything for the current easing; only rebuilds the tab body when the tab changes. */
  private refresh(): void {
    const tab = this.easing ? tabOf(this.easing) : null;
    if (tab !== this.tab || this.syncs.length === 0) {
      this.tab = tab;
      this.renderTabs();
      this.renderBody();
    }
    this.updateVisuals();
  }

  /** Applies a new easing from the user and tells the owner. */
  private emit(easing: Easing, coalesceKey = EDIT_KEY): void {
    this.easing = easing;
    this.onChange(easing, coalesceKey);
    const tab = tabOf(easing);
    if (tab !== this.tab) {
      this.tab = tab;
      this.renderTabs();
      this.renderBody();
    }
    this.updateVisuals();
  }

  /** Builds the tab buttons. */
  private renderTabs(): void {
    this.tabs.replaceChildren(
      ...TABS.map((tab) => {
        const button = h(
          'button',
          {
            type: 'button',
            role: 'tab',
            class: 'easing-tab',
            'aria-selected': String(tab.id === this.tab),
          },
          tab.label,
        );
        button.addEventListener('click', () => {
          if (tab.id !== this.tab) this.emit(defaultForTab(tab.id), 'easing:tab');
        });
        return button;
      }),
    );
  }

  /** Updates the graph, handles, inputs and preview for the current easing. */
  private updateVisuals(): void {
    const easing = this.easing;
    this.curve.setAttribute('d', easing ? graphPath(easing) : '');
    this.renderHandles();
    for (const sync of this.syncs) sync();
    if (this.reducedMotion) this.placeDots(1);
  }

  /** Draws the two control handles for curve easings. */
  private renderHandles(): void {
    const points = this.easing && this.tab === 'curve' ? controlPointsOf(this.easing) : null;
    if (!points) {
      this.handles.replaceChildren();
      return;
    }
    const [x1, y1, x2, y2] = points;
    const start = toGraph(0, 0);
    const end = toGraph(1, 1);
    const a = toGraph(x1, y1);
    const b = toGraph(x2, y2);
    const handle = (which: 1 | 2, at: { px: number; py: number }): SVGCircleElement => {
      const circle = svgEl('circle', {
        class: 'easing-handle',
        cx: at.px,
        cy: at.py,
        r: 6,
        tabindex: 0,
        role: 'slider',
        'aria-label': `Control point ${which}`,
        'data-handle': which,
      });
      circle.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        circle.setPointerCapture(event.pointerId);
        this.dragHandle(circle, which);
      });
      return circle;
    };
    this.handles.replaceChildren(
      svgEl('line', { class: 'easing-stem', x1: start.px, y1: start.py, x2: a.px, y2: a.py }),
      svgEl('line', { class: 'easing-stem', x1: end.px, y1: end.py, x2: b.px, y2: b.py }),
      handle(1, a),
      handle(2, b),
    );
  }

  /** Follows the pointer while a control handle is dragged. Shift snaps to 0.05 steps. */
  private dragHandle(circle: SVGCircleElement, which: 1 | 2): void {
    /** Converts the pointer to graph pixels and applies it to the chosen handle. */
    const move = (event: PointerEvent): void => {
      const box = this.graph.getBoundingClientRect();
      const scale = GRAPH_SIZE / (box.width || GRAPH_SIZE);
      const point = dragControlPoint(
        (event.clientX - box.left) * scale,
        (event.clientY - box.top) * scale,
        event.shiftKey,
      );
      const current = (this.easing && controlPointsOf(this.easing)) ?? [0.25, 0.1, 0.25, 1];
      const [x1, y1, x2, y2] = current;
      this.emit(
        which === 1
          ? { type: 'cubic', x1: point.x, y1: point.y, x2, y2 }
          : { type: 'cubic', x1, y1, x2: point.x, y2: point.y },
      );
    };
    /** Ends the drag. */
    const end = (): void => {
      circle.removeEventListener('pointermove', move);
      circle.removeEventListener('pointerup', end);
      circle.removeEventListener('pointercancel', end);
    };
    circle.addEventListener('pointermove', move);
    circle.addEventListener('pointerup', end);
    circle.addEventListener('pointercancel', end);
  }

  /** Builds the controls for the current tab. */
  private renderBody(): void {
    this.syncs = [];
    this.body.replaceChildren();
    const easing = this.easing;
    if (!easing) {
      this.body.append(
        h(
          'p',
          { class: 'easing-note' },
          'The selected keyframes have different easings. Pick one to apply it to all.',
        ),
      );
      this.syncs.push(() => undefined);
      return;
    }
    switch (this.tab) {
      case 'curve':
        this.renderCurve();
        break;
      case 'spring':
        this.renderSpring();
        break;
      case 'bounce':
        this.renderPhysical([
          {
            label: 'Bounces',
            min: 1,
            max: 8,
            step: 1,
            get: (e) => (e.type === 'bounce' ? e.bounces : 3),
            set: (e, v) => ({ ...asBounce(e), bounces: Math.round(v) }),
          },
          {
            label: 'Decay',
            min: 0.1,
            max: 0.9,
            step: 0.05,
            get: (e) => (e.type === 'bounce' ? e.decay : 0.5),
            set: (e, v) => ({ ...asBounce(e), decay: v }),
          },
        ]);
        break;
      case 'elastic':
        this.renderPhysical([
          {
            label: 'Oscillations',
            min: 1,
            max: 10,
            step: 1,
            get: (e) => (e.type === 'elastic' ? e.oscillations : 3),
            set: (e, v) => ({ ...asElastic(e), oscillations: Math.round(v) }),
          },
          {
            label: 'Decay',
            min: 1,
            max: 20,
            step: 0.5,
            get: (e) => (e.type === 'elastic' ? e.decay : 6),
            set: (e, v) => ({ ...asElastic(e), decay: v }),
          },
        ]);
        break;
      case 'steps':
        this.renderSteps();
        break;
      default:
        this.syncs.push(() => undefined);
        break;
    }
  }

  /** A chip button that applies an easing. */
  private chip(label: string, easing: Easing, active: () => boolean): HTMLButtonElement {
    const button = h('button', { type: 'button', class: 'easing-chip' }, label);
    button.addEventListener('click', () => {
      this.emit(easing, 'easing:chip');
    });
    this.syncs.push(() => {
      button.setAttribute('aria-pressed', String(active()));
    });
    return button;
  }

  /** The Curve tab: preset chips, saved easings and the four numeric fields. */
  private renderCurve(): void {
    const matches = (candidate: Easing) => (): boolean => {
      const a = this.easing && controlPointsOf(this.easing);
      const b = controlPointsOf(candidate);
      return !!a && !!b && a.every((v, i) => Math.abs(v - (b[i] ?? 0)) < 0.005);
    };
    const basics = h(
      'div',
      { class: 'easing-chips' },
      ...BASIC_CURVES.map((c) => this.chip(c.label, c.easing, matches(c.easing))),
    );
    const named = h(
      'div',
      { class: 'easing-chips' },
      ...NAMED_CURVES.map((c) => {
        const [x1, y1, x2, y2] = c.values;
        const easing: Easing = { type: 'cubic', x1, y1, x2, y2 };
        return this.chip(c.label, easing, matches(easing));
      }),
    );
    const savedRow = h('div', { class: 'easing-chips easing-saved' });
    const renderSaved = (): void => {
      savedRow.replaceChildren(
        ...this.saved.map((entry) => {
          const chip = this.chip(entry.name, entry.easing, () =>
            sameEasing(this.easing, entry.easing),
          );
          const remove = h(
            'button',
            { type: 'button', class: 'easing-chip-remove', 'aria-label': `Remove ${entry.name}` },
            '×',
          );
          remove.addEventListener('click', () => {
            this.saved = removeSavedEasing(entry.name);
            renderSaved();
          });
          return h('span', { class: 'easing-saved-chip' }, chip, remove);
        }),
      );
    };
    renderSaved();

    const fields = (['x1', 'y1', 'x2', 'y2'] as const).map((key, index) => {
      const input = new NumberInput({
        label: key,
        min: index % 2 === 0 ? 0 : Y_MIN,
        max: index % 2 === 0 ? 1 : Y_MAX,
        step: 0.01,
        onChange: (value) => {
          const [x1, y1, x2, y2] = (this.easing && controlPointsOf(this.easing)) ?? [
            0.25, 0.1, 0.25, 1,
          ];
          const next = [x1, y1, x2, y2];
          next[index] = value;
          this.emit({
            type: 'cubic',
            x1: next[0] ?? x1,
            y1: next[1] ?? y1,
            x2: next[2] ?? x2,
            y2: next[3] ?? y2,
          });
        },
      });
      this.syncs.push(() => {
        const points = this.easing && controlPointsOf(this.easing);
        input.setValue(points ? (points[index] ?? 0) : null);
      });
      return input.el;
    });

    this.body.append(
      basics,
      named,
      h('div', { class: 'easing-fields' }, ...fields),
      this.saveRow(renderSaved),
      savedRow,
    );
  }

  /** The "Save easing" name field and button. */
  private saveRow(onSaved: () => void): HTMLElement {
    const name = h('input', {
      class: 'easing-save-name',
      placeholder: 'Name',
      'aria-label': 'Saved easing name',
      maxlength: 40,
    });
    const save = h('button', { type: 'button', class: 'easing-save' }, 'Save easing');
    save.addEventListener('click', () => {
      if (!this.easing || name.value.trim() === '') return;
      this.saved = saveEasing(name.value, this.easing);
      name.value = '';
      onSaved();
    });
    return h('div', { class: 'easing-save-row' }, name, save);
  }

  /** A slider and number field pair for one easing parameter. */
  private slider(
    label: string,
    range: { min: number; max: number; step: number },
    read: () => number,
    write: (value: number) => void,
  ): HTMLElement {
    const slider = h('input', {
      type: 'range',
      min: range.min,
      max: range.max,
      step: range.step,
      'aria-label': `${label} slider`,
    });
    const number = new NumberInput({ label, ...range, onChange: write });
    slider.addEventListener('input', () => {
      write(Number(slider.value));
    });
    this.syncs.push(() => {
      slider.value = String(read());
      number.setValue(read());
    });
    return h('div', { class: 'easing-slider' }, number.el, slider);
  }

  /** The Spring tab: stiffness, damping and mass, with presets. */
  private renderSpring(): void {
    /** Replaces one spring parameter. */
    const set = (key: 'stiffness' | 'damping' | 'mass', value: number): void => {
      const e =
        this.easing?.type === 'spring'
          ? this.easing
          : { type: 'spring' as const, stiffness: 170, damping: 26, mass: 1 };
      this.emit({ ...e, [key]: value });
    };
    /** Reads one spring parameter. */
    const get = (key: 'stiffness' | 'damping' | 'mass') => (): number =>
      this.easing?.type === 'spring' ? this.easing[key] : 0;
    this.body.append(
      h(
        'div',
        { class: 'easing-chips' },
        ...SPRING_PRESETS.map((p) => {
          const easing: Easing = {
            type: 'spring',
            stiffness: p.stiffness,
            damping: p.damping,
            mass: p.mass,
          };
          return this.chip(p.label, easing, () => sameEasing(this.easing, easing));
        }),
      ),
      this.slider('Stiffness', { min: 1, max: 1000, step: 1 }, get('stiffness'), (v) => {
        set('stiffness', v);
      }),
      this.slider('Damping', { min: 1, max: 100, step: 1 }, get('damping'), (v) => {
        set('damping', v);
      }),
      this.slider('Mass', { min: 0.1, max: 10, step: 0.1 }, get('mass'), (v) => {
        set('mass', v);
      }),
      h('p', { class: 'easing-note' }, 'Springs are stretched to fit the time between keyframes.'),
    );
  }

  /** The Bounce and Elastic tabs: a slider and number field per parameter. */
  private renderPhysical(
    params: {
      label: string;
      min: number;
      max: number;
      step: number;
      get: (easing: Easing) => number;
      set: (easing: Easing, value: number) => Easing;
    }[],
  ): void {
    for (const param of params) {
      this.body.append(
        this.slider(
          param.label,
          param,
          () => (this.easing ? param.get(this.easing) : 0),
          (value) => {
            if (this.easing) this.emit(param.set(this.easing, value));
          },
        ),
      );
    }
  }

  /** The Steps tab: count and jump position. `jump-none` needs at least two steps. */
  private renderSteps(): void {
    const count = new NumberInput({
      label: 'Steps',
      min: 1,
      max: 100,
      step: 1,
      onChange: (value) => {
        const e =
          this.easing?.type === 'steps'
            ? this.easing
            : { type: 'steps' as const, count: 4, position: 'jump-end' as const };
        const min = e.position === 'jump-none' ? 2 : 1;
        this.emit({ ...e, count: Math.max(min, Math.round(value)) });
      },
    });
    const position = h(
      'select',
      { 'aria-label': 'Step position' },
      ...(['jump-end', 'jump-start', 'jump-none', 'jump-both'] as const).map((p) =>
        h('option', { value: p }, p.replace('jump-', 'jump ')),
      ),
    );
    position.addEventListener('change', () => {
      const e =
        this.easing?.type === 'steps'
          ? this.easing
          : { type: 'steps' as const, count: 4, position: 'jump-end' as const };
      const next = position.value as typeof e.position;
      this.emit({
        ...e,
        position: next,
        count: next === 'jump-none' ? Math.max(2, e.count) : e.count,
      });
    });
    this.syncs.push(() => {
      if (this.easing?.type !== 'steps') return;
      count.setValue(this.easing.count);
      position.value = this.easing.position;
    });
    this.body.append(h('div', { class: 'easing-fields' }, count.el, position));
  }

  /** Starts the looping preview dot. */
  private startPreview(): void {
    cancelAnimationFrame(this.frame);
    this.previewStart = 0;
    this.idleFrames = 0;
    this.frame = requestAnimationFrame((now) => {
      this.tick(now);
    });
  }

  /** Places the two preview dots at a fraction of the track. */
  private placeDots(progress: number, linear = progress): void {
    const eased = this.easing ? evaluateEasing(this.easing, progress) : linear;
    this.dot.style.left = `${eased * 100}%`;
    this.ghost.style.left = `${linear * 100}%`;
  }

  /** Advances the preview and stops when the control leaves the page. */
  private tick(now: number): void {
    if (this.el.isConnected) this.wasConnected = true;
    else if (this.wasConnected || ++this.idleFrames > 120) return;
    if (this.previewStart === 0) this.previewStart = now;
    const elapsed = now - this.previewStart;
    const cycle = PREVIEW_CYCLE_MS + PREVIEW_PAUSE_MS;
    const phase = elapsed % cycle;
    const t = Math.min(1, phase / PREVIEW_CYCLE_MS);
    this.placeDots(t);
    if (this.previewOnce && elapsed >= PREVIEW_CYCLE_MS) {
      this.previewOnce = false;
      this.placeDots(1);
      return;
    }
    this.frame = requestAnimationFrame((time) => {
      this.tick(time);
    });
  }
}
